import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { pushData } from '../utilities/pushData';
import { TYPES } from '../reducer';

// Utilities
import addInspectors from './addInspectors';
import addProjects from './addProjects';
import filterInspectors from './filterInspectors';

// Icons & Helpers for Highlighting logic
import { createCustomMarkerElement } from '../createCustomMarkerElement';
import * as Icons from './mapIcons';

export default function SchedulingMap({ mapInstance }) {
  const dispatch = useDispatch();

  // Redux Selectors
  const projects = useSelector(state => state.projects);
  const inspectors = useSelector(state => state.inspectors);
  
  // We grab IDs separately to verify changes efficiently
  const selectedInspectorId = useSelector(state => state.inspector?.id);
  const selectedProjectId = useSelector(state => state.project?.id);

  // Local State for Pins
  const [projectPins, setProjectPins] = useState({});
  const [inspectorPins, setInspectorPins] = useState({});
  
  // UI State
  const [showProjects, setShowProjects] = useState(true);
  const [showInspectors, setShowInspectors] = useState(true);
  const [copiedMessage, setCopiedMessage] = useState(false);

  // Helper for clipboard
  const showCopied = () => {
    setCopiedMessage(true);
    setTimeout(() => setCopiedMessage(false), 2000);
  };

  // ----------------------------------------------------------------
  // 1. PROJECT MARKERS (Create/Destroy only when List changes)
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!mapInstance) return;

    const handleProjectClick = (p) => {
      const details = [
        `Address: ${p?.site?.dummyAddress ?? ''}`,
        `Phone: ${p?.site?.siteRepPhone}`,
        `Email: ${p?.site?.siteRepEmail}`
      ].join('\n');
      
      if (navigator?.clipboard) {
        navigator.clipboard.writeText(details);
        showCopied();
      }
      dispatch(pushData({ project: p }));
    };

    // NOTE: We pass project: {} so markers are created 'unselected' initially.
    // We will handle selection in a separate effect.
    const newPins = addProjects(
      { project: {}, projects, map: mapInstance }, 
      projectPins, 
      handleProjectClick, 
      showProjects
    );
    
    setProjectPins(newPins);
  }, [projects, showProjects, mapInstance, dispatch]); // NO selectedProjectId dependency!


  // ----------------------------------------------------------------
  // 2. INSPECTOR MARKERS (Create/Destroy only when List changes)
  // ----------------------------------------------------------------
  useEffect(async () => {
    if (!mapInstance) return;

    const handleInspectorClick = (i) => {
      const details = [
        `Name: ${i.name ?? ''}`,
        `Phone: ${i.phone ?? ''}`,
        `Email: ${i.email ?? ''}`,
      ].join('\n');
      
      if (navigator?.clipboard) {
        navigator.clipboard.writeText(details);
        showCopied();
      }
      dispatch(pushData({ inspector: i }));
    };

    // NOTE: Passing inspector: {} to avoid baking selection into the marker creation
    const [filtered, newPins] = await addInspectors(
      { inspector: {}, inspectors, map: mapInstance }, 
      inspectorPins, 
      handleInspectorClick, 
      showInspectors
    );
    
    dispatch({ type: TYPES.SET_FILTERED_INSPECTORS, payload: filtered });
    setInspectorPins(newPins);
  }, [inspectors, showInspectors, mapInstance, dispatch]); // NO selectedInspectorId dependency!


  // ----------------------------------------------------------------
  // 3. SELECTION HIGHLIGHTING (Fast Update)
  // ----------------------------------------------------------------
  
  // Highlight Inspector
  useEffect(() => {
    Object.keys(inspectorPins).forEach(pinId => {
      const marker = inspectorPins[pinId];
      if (!marker) return;

      // Check if this is the selected one
      const isSelected = String(pinId) === String(selectedInspectorId);
      
      // Select the correct icon function
      const iconFunc = isSelected ? Icons.inspectorSelected : Icons.inspectorImage;
      
      // Update the AdvancedMarkerElement content directly
      // This is cheap and does not require destroying the marker
      marker.content = createCustomMarkerElement(iconFunc());
      marker.zIndex = isSelected ? 100 : 1; 
    });
  }, [selectedInspectorId, inspectorPins]);

  // Highlight Project
  useEffect(() => {
    Object.keys(projectPins).forEach(pinId => {
      const marker = projectPins[pinId];
      if (!marker) return;

      const isSelected = String(pinId) === String(selectedProjectId);
      const iconFunc = isSelected ? Icons.selectedImage : Icons.image;
      
      marker.content = createCustomMarkerElement(iconFunc());
      marker.zIndex = isSelected ? 100 : 1;
    });
  }, [selectedProjectId, projectPins]);


  // ----------------------------------------------------------------
  // 4. BOUNDS LISTENER (Filtering Logic)
  // ----------------------------------------------------------------
  useEffect(() => {
      if (!mapInstance) return;

      const listener = mapInstance.addListener('bounds_changed', function () {
        // We use your existing filter utility
        // This is fast because it's just math, no DOM manipulation
        const filtered = filterInspectors(mapInstance, inspectors);
        
        // We debounce this slightly in practice, but raw is fine if logic is simple
        dispatch({ type: TYPES.SET_FILTERED_INSPECTORS, payload: filtered });
      });
      
      return () => window.google.maps.event.removeListener(listener);
    },
    [mapInstance, inspectors, dispatch]
  );


  // ----------------------------------------------------------------
  // RENDER
  // ----------------------------------------------------------------
  return (
    <div>
       {/* Controls Overlay */}
        <div
          style={{
            position: 'absolute',
            bottom: 10,
            left: 100,
            zIndex: 1000,
            display: 'flex',
            gap: 10,
          }}
        >
          <button
            onClick={() => setShowProjects(!showProjects)}
            style={{ padding: '8px 12px', background: 'white', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
          >
            {showProjects ? 'Hide' : 'Show'} Projects
          </button>
          <button
            onClick={() => setShowInspectors(!showInspectors)}
            style={{ padding: '8px 12px', background: 'white', border: '1px solid #ccc', borderRadius: '4px', cursor: 'pointer' }}
          >
            {showInspectors ? 'Hide' : 'Show'} Inspectors
          </button>
        </div>

        {copiedMessage && (
          <div
            style={{
              position: 'absolute', top: 20, right: 20,
              backgroundColor: '#4caf50', color: 'white',
              padding: '8px 16px', borderRadius: '8px',
              zIndex: 1000, opacity: 0.98,
            }}
          >
            Copied to clipboard
          </div>
        )}
    </div>
  );
}